import { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ExternalLink } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

class Properties {
  list: any;
  /** Deckelt die Wertspalte (z. B. "350px"); ohne Angabe füllt sie die Restbreite. */
  valueWidth?: string;
  className?: string;
}

/**
 * Label/Wert-Liste der Objektseiten als echte Beschreibungsliste (`dl`).
 *
 * Das Raster ist `max-content 1fr`: das Label bleibt so breit wie sein
 * längster Text (statt der früheren festen 200px, an denen lange Labels
 * abgeschnitten wurden), der Wert nimmt den Rest. Unterhalb `sm` fällt die
 * Liste einspaltig — auf 360px hätten zwei Spalten den Wert auf wenige
 * Zeichen gequetscht. `min-w-0` + `break-words` halten lange Werte (URLs,
 * Adressen) im Container, damit die Seite nicht horizontal scrollt.
 */
export default function InfoList(properties: Properties) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const list: any[] = Array.isArray(properties.list) ? properties.list : [];

  return (
    <dl
      className={cn(
        "grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-[max-content_1fr]",
        properties.className,
      )}
    >
      {list.map((item: any, index: number) => {
        const width = item.valueWidth || properties.valueWidth;
        const alignRight = item.align === "right";
        const value = (
          <span
            className="min-w-0 break-words"
            style={item.color ? { color: item.color } : undefined}
          >
            {item.value}
            {item.suffix ? <> {item.suffix}</> : null}
          </span>
        );

        return (
          <Fragment key={`${item.label ?? ""}-${index}`}>
            {/* Auf schmalen Viewports stehen Label und Wert untereinander —
                der Abstand nach oben trennt dann die Paare voneinander. */}
            <dt className="min-w-0 break-words font-bold max-sm:mt-2 max-sm:first:mt-0">
              {item.label}:
            </dt>
            <dd
              className={cn(
                "flex min-w-0 items-center gap-1",
                alignRight ? "justify-end text-right" : "justify-start",
              )}
              style={width ? { maxWidth: width } : undefined}
            >
              {item.link ? (
                <a
                  className="min-w-0 break-words underline underline-offset-2 hover:no-underline"
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {value}
                </a>
              ) : (
                value
              )}
              {!item.link && item.navigationLink ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 max-sm:h-11 max-sm:w-11"
                  aria-label={t("infoList.openLinked")}
                  onClick={() => navigate(item.navigationLink)}
                >
                  <ExternalLink aria-hidden="true" className="h-4 w-4" />
                </Button>
              ) : null}
            </dd>
          </Fragment>
        );
      })}
    </dl>
  );
}
